// TMOS Setup Wizard Modal
// Date: December 19, 2025
// Purpose: Modal component for setup wizard with step-by-step flow

import React, { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { setupWizardService, SetupType, SetupStep, SetupProgress } from '@/services/setupWizardService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

// Import step components (to be created)
import { WelcomeStep } from './steps/WelcomeStep';
import { OrganizationBasicsStep } from './steps/OrganizationBasicsStep';
import { HierarchySelectionStep } from './steps/HierarchySelectionStep';
import { KPISetupStep } from './steps/KPISetupStep';
import { PositionRoleMappingStep } from './steps/PositionRoleMappingStep';
import { UserRolePermissionsStep } from './steps/UserRolePermissionsStep';
import { UserSetupStep } from './steps/UserSetupStep';
import { PMOSetupStep } from './steps/PMOSetupStep';

interface SetupWizardModalProps {
  isOpen: boolean;
  onClose: () => void;
  setupType: SetupType;
  onComplete?: () => void;
  onSkip?: () => void;
}

/**
 * SetupWizardModal Component
 * 
 * Multi-step modal wizard for user and tenant setup.
 * Supports different setup types: TENANT_ADMIN, TENANT_USER, INVITED_USER
 */
export const SetupWizardModal: React.FC<SetupWizardModalProps> = ({
  isOpen,
  onClose,
  setupType,
  onComplete,
  onSkip
}) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState<SetupStep[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [progress, setProgress] = useState<SetupProgress | null>(null);
  const [setupData, setSetupData] = useState<Record<string, any>>({});
  const [canSkip, setCanSkip] = useState(false);

  // Load setup steps and progress on mount
  useEffect(() => {
    if (isOpen) {
      loadSetupData();
    }
  }, [isOpen, setupType]);

  const loadSetupData = async () => {
    try {
      setLoading(true);

      // Load setup steps
      const loadedSteps = await setupWizardService.getSetupSteps(setupType);
      setSteps(loadedSteps);

      // Load or initialize progress
      let currentProgress = await setupWizardService.getUserSetupProgress(setupType);
      if (!currentProgress) {
        currentProgress = await setupWizardService.initializeUserSetup(setupType);
      }
      setProgress(currentProgress);
      setSetupData(currentProgress.setup_data || {});

      // Set current step index based on progress
      if (currentProgress.current_step_id) {
        const stepIndex = loadedSteps.findIndex(s => s.step_id === currentProgress.current_step_id);
        if (stepIndex >= 0) {
          setCurrentStepIndex(stepIndex);
        }
      }

      // Determine if setup can be skipped
      const requiredSteps = loadedSteps.filter(s => s.is_required);
      setCanSkip(requiredSteps.length === 0);

    } catch (error: any) {
      console.error('Error loading setup data:', error);
      toast.error(error.message || 'Failed to load setup wizard');
    } finally {
      setLoading(false);
    }
  };

  const handleStepComplete = async (stepData: Record<string, any>) => {
    try {
      setLoading(true);

      const currentStep = steps[currentStepIndex];
      if (!currentStep) return;

      // Update setup data
      const updatedData = { ...setupData, ...stepData };
      setSetupData(updatedData);

      // Complete the step
      const updatedProgress = await setupWizardService.completeStep(
        setupType,
        currentStep.step_id,
        stepData
      );
      setProgress(updatedProgress);

      // Move to next step or complete
      if (currentStepIndex < steps.length - 1) {
        setCurrentStepIndex(prev => prev + 1);
      } else {
        // All steps complete
        await handleComplete();
      }

    } catch (error: any) {
      console.error('Error completing step:', error);
      toast.error(error.message || 'Failed to complete step');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    try {
      setLoading(true);
      await setupWizardService.completeUserSetup(setupType);
      toast.success(t('setupWizard.completed') || 'Setup completed successfully');
      onComplete?.();
      onClose();
    } catch (error: any) {
      console.error('Error completing setup:', error);
      toast.error(error.message || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      setLoading(true);
      await setupWizardService.skipUserSetup(setupType);
      toast.success(t('setupWizard.skipped') || 'Setup skipped');
      onSkip?.();
      onClose();
    } catch (error: any) {
      console.error('Error skipping setup:', error);
      toast.error(error.message || 'Failed to skip setup');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentStep = steps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;
  const completionPercentage = progress?.completion_percentage || 0;

  // Render step component based on step_id
  const renderStepComponent = () => {
    if (!currentStep) return null;

    const stepProps = {
      setupData,
      onComplete: handleStepComplete,
      onNext: handleNext,
      loading
    };

    switch (currentStep.step_id) {
      case 'welcome':
        return <WelcomeStep {...stepProps} />;
      case 'organization_basics':
        return <OrganizationBasicsStep {...stepProps} />;
      case 'hierarchy_selection':
      case 'hierarchy_assignment':
        return <HierarchySelectionStep {...stepProps} />;
      case 'kpi_setup':
        return <KPISetupStep {...stepProps} />;
      case 'position_role_mapping':
      case 'position_assignment':
        return <PositionRoleMappingStep {...stepProps} />;
      case 'user_role_permissions':
      case 'role_assignment':
        return <UserRolePermissionsStep {...stepProps} />;
      case 'user_setup':
        return <UserSetupStep {...stepProps} />;
      case 'pmo_setup':
        return <PMOSetupStep {...stepProps} />;
      default:
        return <div>Step: {currentStep.step_name}</div>;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('setupWizard.title') || 'Setup Wizard'}
      size="xl"
      className="max-w-4xl"
    >
      {loading && !progress ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-[var(--color-secondary-text)]">Loading setup wizard...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[var(--color-surface-text)]">
                {t('setupWizard.progress') || 'Progress'}: {completionPercentage}%
              </span>
              <span className="text-sm text-[var(--color-secondary-text)]">
                {currentStepIndex + 1} / {steps.length}
              </span>
            </div>
            <div className="w-full bg-[var(--color-secondary-bg)] rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>

          {/* Step Indicator */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        index < currentStepIndex
                          ? 'bg-green-500 text-white'
                          : index === currentStepIndex
                          ? 'bg-blue-600 text-white'
                          : 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]'
                      }`}
                    >
                      {index < currentStepIndex ? '✓' : index + 1}
                    </div>
                    <span className="text-xs mt-1 text-center text-[var(--color-secondary-text)] max-w-[80px] truncate">
                      {step.step_name}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`h-0.5 flex-1 mx-2 ${
                        index < currentStepIndex ? 'bg-green-500' : 'bg-[var(--color-secondary-bg)]'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="min-h-[400px]">
            {currentStep && (
              <div>
                <h3 className="text-lg font-semibold mb-2 text-[var(--color-surface-text)]">
                  {currentStep.step_name}
                </h3>
                {currentStep.step_description && (
                  <p className="text-sm text-[var(--color-secondary-text)] mb-6">
                    {currentStep.step_description}
                  </p>
                )}
                {renderStepComponent()}
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[var(--color-border)]">
            <div className="flex items-center gap-3">
              {canSkip && (
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  disabled={loading}
                >
                  {t('setupWizard.skip') || 'Skip Setup'}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                {t('common.cancel') || 'Cancel'}
              </Button>
            </div>

            <div className="flex items-center gap-3">
              {!isFirstStep && (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={loading}
                >
                  <ChevronLeftIcon className="w-4 h-4 mr-1" />
                  {t('common.previous') || 'Previous'}
                </Button>
              )}
              {!isLastStep && (
                <Button
                  onClick={handleNext}
                  disabled={loading}
                >
                  {t('common.next') || 'Next'}
                  <ChevronRightIcon className="w-4 h-4 ml-1" />
                </Button>
              )}
              {isLastStep && (
                <Button
                  onClick={handleComplete}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {t('setupWizard.complete') || 'Complete Setup'}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

